// src/pages/finance-portal/customers/Customers.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, IconButton, Alert, Skeleton, Tabs, Tab,
    Tooltip, InputAdornment,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';
import { useNavigate } from 'react-router-dom';
import CustomerForm from './CustomerForm';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    ACTIVE: { bg: '#dcfce7', color: '#166534' },
    INACTIVE: { bg: '#f1f5f9', color: '#64748b' },
    ARCHIVED: { bg: '#fecaca', color: '#991b1b' },
};

const STATUS_TABS = ['ALL', 'ACTIVE', 'INACTIVE', 'ARCHIVED'];

function formatCurrency(val, currency = 'USD') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(val || 0);
}

export default function Customers() {
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [statusTab, setStatusTab] = useState(0);

    // Form dialog
    const [formOpen, setFormOpen] = useState(false);
    const [editCustomer, setEditCustomer] = useState(null);

    // Delete dialog
    const [deleteDialog, setDeleteDialog] = useState({ open: false, customer: null });
    const [deleting, setDeleting] = useState(false);

    const fetchCustomers = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            const currentStatus = STATUS_TABS[statusTab];
            if (currentStatus !== 'ALL') params.status = currentStatus;
            if (search) params.search = search;

            const res = await axiosInstance.get('/api/finance/customers', { params });
            if (res.data.success) {
                setCustomers(res.data.data);
                setTotal(res.data.total);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load customers');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, statusTab, search]);

    useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await axiosInstance.delete(`/api/finance/customers/${deleteDialog.customer.id}`);
            setDeleteDialog({ open: false, customer: null });
            fetchCustomers();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete customer');
        } finally {
            setDeleting(false);
        }
    };

    const handleEdit = (e, customer) => {
        e.stopPropagation();
        setEditCustomer(customer);
        setFormOpen(true);
    };

    const handleAddNew = () => {
        setEditCustomer(null);
        setFormOpen(true);
    };

    const handleRowClick = (customer) => {
        navigate(`/finance/customers/${customer.id}`);
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PeopleIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                Customers
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                Manage your finance customers
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddNew}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: 'rgba(255,255,255,0.2)', color: '#fff',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        Add Customer
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                <Alert
                    severity="info"
                    icon={<LinkIcon sx={{ fontSize: 20 }} />}
                    sx={{ mb: 2, borderRadius: '12px', fontFamily, fontSize: '0.8rem', bgcolor: '#f0f9ff', border: '1px solid #bae6fd' }}
                >
                    AR Customers are linked to your <strong>CRM Contacts</strong>. Click "Add Customer" to select a contact from your CRM, or create a standalone customer.
                </Alert>

                {/* Search Bar */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="Search customers..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                            sx: { fontFamily, fontSize: '0.85rem', borderRadius: '10px' },
                        }}
                        sx={{ minWidth: 280 }}
                    />
                </Box>

                {/* Status Tabs */}
                <Tabs
                    value={statusTab}
                    onChange={(_, val) => { setStatusTab(val); setPage(0); }}
                    sx={{
                        mb: 2,
                        '& .MuiTab-root': {
                            fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.85rem',
                            color: '#64748b', minHeight: 40, py: 1,
                        },
                        '& .Mui-selected': { color: ACCENT },
                        '& .MuiTabs-indicator': { bgcolor: ACCENT, height: 3, borderRadius: '3px' },
                        minHeight: 40,
                    }}
                >
                    {STATUS_TABS.map(tab => (
                        <Tab key={tab} label={tab.charAt(0) + tab.slice(1).toLowerCase()} />
                    ))}
                </Tabs>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Customer #', 'Name', 'Email', 'Phone', 'Payment Terms', 'Credit Limit', 'Status', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0', letterSpacing: '0.05em' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                                        <TableRow key={i}>
                                            <TableCell colSpan={8}><Skeleton height={36} /></TableCell>
                                        </TableRow>
                                    ))
                                ) : customers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                            <PeopleIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>No customers found</Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.8rem', mt: 0.5 }}>Add your first customer to get started</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : customers.map(customer => {
                                    const statusStyle = STATUS_COLORS[customer.status] || STATUS_COLORS.INACTIVE;
                                    const displayName = customer.company_name
                                        ? customer.company_name
                                        : `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
                                    return (
                                        <TableRow
                                            key={customer.id}
                                            hover
                                            onClick={() => handleRowClick(customer)}
                                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
                                        >
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                {customer.customer_number || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Box>
                                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>
                                                            {displayName || '-'}
                                                        </Typography>
                                                        {customer.company_name && customer.first_name && (
                                                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                                                                {customer.first_name} {customer.last_name}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    {customer.crm_contact_id && (
                                                        <Tooltip title={`Linked to CRM: ${customer.crm_contact_name || 'Contact'}`}>
                                                            <Chip
                                                                icon={<LinkIcon sx={{ fontSize: 12 }} />}
                                                                label="CRM"
                                                                size="small"
                                                                sx={{ fontFamily, fontSize: '0.6rem', fontWeight: 600, height: 18, bgcolor: '#dbeafe', color: '#1d4ed8', '& .MuiChip-icon': { color: '#1d4ed8', fontSize: 10 } }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {customer.email || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {customer.phone || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>
                                                {customer.payment_terms ? customer.payment_terms.replace(/_/g, ' ') : '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                                {customer.credit_limit ? formatCurrency(customer.credit_limit, customer.currency || 'USD') : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={customer.status}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                                        bgcolor: statusStyle.bg, color: statusStyle.color,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={(e) => handleEdit(e, customer)} sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, customer }); }}
                                                            sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* Customer Form Dialog */}
            <CustomerForm
                open={formOpen}
                onClose={() => { setFormOpen(false); setEditCustomer(null); }}
                onSaved={fetchCustomers}
                customer={editCustomer}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, customer: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    Delete {deleteDialog.customer?.company_name || `${deleteDialog.customer?.first_name || ''} ${deleteDialog.customer?.last_name || ''}`.trim()}?
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.9rem' }}>
                        This will permanently delete this customer and cannot be undone. Customers with existing invoices or payments cannot be deleted.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialog({ open: false, customer: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deleting}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
